import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Container, Form, Button, Collapse } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Breadcrumbs from '../../../components/common/Breadcrumb';
import { isEmpty, debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import axios from 'axios';
import Select from 'react-select';
import { customStylesForm } from '../../../components/common/customStyleSelect';

// Icons
import { MdAdd, MdSearch } from 'react-icons/md';

// Components
import ModalDelete from '../../../components/common/ModalDelete';
import deepSearchFunction from '../../../components/common/SearchFunction';
import LoadingState from '../../../components/common/LoadingState';
import Users from './components/tables/Users';

// API CALL
import {
  fetchUsers,
  removeUser,
  updateUser,
  setKeywords,
  setPage,
} from '../../../redux/slices/usersSlice';
import { getMinistries, getDepartments } from '../../../redux/slices/utilsSlice';

const MainPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showRemove, setShowRemove] = useState(false);
  const [item, setItem] = useState(null);
  const [notActive, setNotActive] = useState(false);
  const [goToPage, setGoToPage] = useState('');
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    ministry_id: null,
    department_id: null,
    role: null,
  });

  const { page, perPage, keywords, users, btnLoading, ministries, departments } = useSelector(
    (state) => ({
      btnLoading: state.usersReducer.btnLoading,
      users: state.usersReducer.users,
      page: state.usersReducer.page,
      perPage: state.usersReducer.perPage,
      keywords: state.usersReducer.keywords,
      ministries: state.utilsReducer.dropdownItems.ministries,
      departments: state.utilsReducer.dropdownItems.departments,
    })
  );

  // Debounced search that actually gets called
  const handleSearchDebounced = useCallback(
    debounce((text) => {
      dispatch(setKeywords(text));
      dispatch(setPage(1));
    }, 400),
    [dispatch]
  );

  // Clear search
  const handleClearSearch = () => {
    setLocalSearchValue('');
    dispatch(setKeywords(''));
    dispatch(setPage(1));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [key]: value,
      };

      // Reset department when ministry changes
      if (key === 'ministry_id') {
        newFilters.department_id = null;
      }

      return newFilters;
    });
    dispatch(setPage(1));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      ministry_id: null,
      department_id: null,
      role: null,
    });
    dispatch(setPage(1));
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter((v) => v !== null).length;
  const hasActiveFilters = activeFilterCount > 0;

  const handleAddUser = () => {
    navigate('/admin/pengguna/baru');
  };

  const handleEditUser = (user) => {
    navigate(`/admin/pengguna/kemaskini/${user.id}`);
  };

  const handleResetState = () => {
    setItem(null);
  };

  const handleRemoveUser = (user) => {
    setShowRemove(true);
    setItem(user);
  };

  const handleCloseRemove = () => {
    setShowRemove(false);
    handleResetState();
  };

  const handleConfirmRemove = async (data) => {
    await dispatch(removeUser(data));
    handleCloseRemove();
  };

  const handleViewNotActive = () => {
    if (!notActive) {
      setNotActive(true);
    } else {
      setNotActive(false);
    }
  };

  const handleActivatedUser = (user) => {
    const newValue = {
      ...user,
      is_deleted: !user.is_deleted,
      group_role: user.group_role.id,
    };
    dispatch(updateUser(newValue));
  };

  useEffect(() => {
    let source = axios.CancelToken.source();

    const initialize = async () => {
      const params = {
        page: page,
        per_page: perPage,
        keywords: keywords,
        not_active: notActive,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...filters,
      };

      // Remove null values from params
      Object.keys(params).forEach((key) => {
        if (params[key] === null || params[key] === '') {
          delete params[key];
        }
      });

      const props = {
        source: source.token,
        params,
      };

      await dispatch(fetchUsers(props));
    };

    initialize();

    setGoToPage(page);

    return () => {
      source.cancel();
    };
  }, [dispatch, page, perPage, notActive, keywords, sortBy, sortOrder, filters]);

  // Sync local search value with Redux keywords (for external changes)
  useEffect(() => {
    setLocalSearchValue(keywords);
  }, []); // Only run on mount to preserve manual input

  // Fetch dropdown data on mount
  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchDropdownData = async () => {
      const props = { source: source.token };
      await Promise.all([dispatch(getMinistries(props)), dispatch(getDepartments(props))]);
    };

    fetchDropdownData();

    return () => {
      source.cancel();
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      <ModalDelete
        showRemove={showRemove}
        closeRemove={handleCloseRemove}
        data={item}
        confirmRemove={handleConfirmRemove}
        btnLoading={btnLoading}
        module="user"
      />

      <Helmet title="Senarai Pengguna" />
      <Container fluid className="p-0">
        <Breadcrumbs title="Sistem Admin" breadcrumbItem="Senarai Pengguna" />
        <Card className="mt-3">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="search-box-custom">
                  <div className="position-relative">
                    <label htmlFor="search-bar-0" className="search-label-custom">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Cari (nama, emel, No. MyKad)..."
                        value={localSearchValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocalSearchValue(value);
                          handleSearchDebounced(value);
                        }}
                        style={{ width: 280 }}
                      />
                    </label>
                    {localSearchValue && (
                      <button
                        type="button"
                        className="position-absolute end-0 text-decoration-none btn btn-link fw-bold"
                        onClick={handleClearSearch}
                        style={{ fontSize: 18, lineHeight: 1 }}
                      >
                        ×
                      </button>
                    )}
                    {!localSearchValue && <MdSearch className="search-icon-2" size={18} />}
                  </div>
                </div>
                <Form.Select
                  className="d-inline-block w-auto"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                    dispatch(setPage(1));
                  }}
                  style={{ width: 180 }}
                >
                  <option value="name-asc">Nama (A-Z)</option>
                  <option value="name-desc">Nama (Z-A)</option>
                  <option value="email-asc">Emel (A-Z)</option>
                  <option value="email-desc">Emel (Z-A)</option>
                  <option value="created_at-desc">Terbaru</option>
                  <option value="created_at-asc">Terlama</option>
                </Form.Select>

                <Button
                  variant={hasActiveFilters ? 'secondary' : 'outline-secondary'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filter
                  {hasActiveFilters && (
                    <span className="badge bg-danger ms-1">{activeFilterCount}</span>
                  )}
                </Button>

                <Tooltip title="Tunjuk pengguna tidak aktif" arrow>
                  <Button
                    variant={`${notActive ? 'danger' : 'outline-danger'}`}
                    onClick={handleViewNotActive}
                  >
                    Tidak Aktif
                  </Button>
                </Tooltip>
              </div>
              <div>
                <Button onClick={handleAddUser}>Daftar Pengguna</Button>
              </div>
            </div>
            <Collapse in={showFilters}>
              <div className="mt-3 pt-3 border-top">
                <div className="d-flex gap-3 flex-wrap">
                  <Form.Group style={{ flex: '1 1 200px' }}>
                    <Form.Label className="fw-bold small">Kementerian</Form.Label>
                    <Select
                      isClearable
                      isSearchable
                      placeholder="Semua Kementerian"
                      options={ministries}
                      value={ministries?.find((m) => m.code === filters.ministry_id) || null}
                      onChange={(val) => handleFilterChange('ministry_id', val?.code || null)}
                      getOptionValue={(opt) => opt.code}
                      getOptionLabel={(opt) => opt.name}
                      styles={customStylesForm}
                    />
                  </Form.Group>
                  <Form.Group style={{ flex: '1 1 200px' }}>
                    <Form.Label className="fw-bold small">Jabatan</Form.Label>
                    <Select
                      isClearable
                      isSearchable
                      placeholder="Semua Jabatan"
                      options={departments}
                      value={departments?.find((d) => d.id === filters.department_id) || null}
                      onChange={(val) => handleFilterChange('department_id', val?.id || null)}
                      getOptionValue={(opt) => opt.id}
                      getOptionLabel={(opt) => opt.value}
                      styles={customStylesForm}
                    />
                  </Form.Group>
                  <div className="d-flex align-items-end">
                    <Button variant="light" onClick={handleClearFilters}>
                      Kosongkan Filter
                    </Button>
                  </div>
                </div>
              </div>
            </Collapse>
          </Card.Header>
          <Card.Body>
            <Users
              handleRemoveUser={handleRemoveUser}
              handleEditUser={handleEditUser}
              handleActivatedUser={handleActivatedUser}
              goToPage={goToPage}
              setGoToPage={setGoToPage}
            />
          </Card.Body>
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default MainPage;
